// src/hooks/useGoogleReviews.ts
'use client';

import { useEffect, useState } from 'react';
import { createClient, SupabaseClient } from '@supabase/supabase-js';

type ReviewRow = {
  id: string;
  location?: string | null;
  reviewer_name?: string | null;
  reviewer_profile_photo?: string | null;
  rating?: string | null; // e.g. 'FIVE'
  comment?: string | null;
  create_time?: string | null; // ISO
  update_time?: string | null;
  raw?: any;
};

export function useGoogleReviews() {
  const [reviews, setReviews] = useState<ReviewRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let mounted = true;

    // Vite uses import.meta.env for client-side env variables
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseAnonKey) {
      console.warn(
        'useGoogleReviews: VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY not set. Reviews will be empty.'
      );
      setReviews([]);
      setLoading(false);
      return;
    }

    const supabase: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
      auth: { persistSession: false },
    });

    const fetchReviews = async () => {
      try {
        const { data, error } = await supabase
          .from<ReviewRow>('google_reviews')
          .select('*')
          .eq('rating', 'FIVE')
          .order('create_time', { ascending: false })
          .limit(10);

        if (error) {
          console.error('useGoogleReviews supabase error:', error.message || error);
          if (mounted) setReviews([]);
          return;
        }

        if (mounted) {
          setReviews(data || []);
        }
      } catch (err: any) {
        console.error('Error fetching Google reviews:', err?.message ?? err);
        if (mounted) setReviews([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchReviews();

    return () => {
      mounted = false;
    };
  }, []);

  return { reviews, loading };
}
